const { SERVICE, ASSIGNMENT, numericStatus } = require('./photographyStatus.cjs');
const { invitationHint, creatorContractError } = require('./serviceOrderFacts.cjs');
const CREATOR_SERVICE_ORDER_STATUS = Object.freeze({
    IDLE: "idle",
    LOADING: "loading",
    READY: "ready",
    EMPTY: "empty",
    ERROR: "error",
    SESSION_EXPIRED: "sessionExpired",
});

const ROLE_META = Object.freeze({
    PHOTOGRAPHY: { roleKey: "photographer", roleLabel: "摄影" },
    PHOTOGRAPHER: { roleKey: "photographer", roleLabel: "摄影" },
    MAKEUP: { roleKey: "makeup", roleLabel: "化妆" },
    MAKEUP_ARTIST: { roleKey: "makeup", roleLabel: "化妆" },
});

const STATUS_LABELS = Object.freeze({ [ASSIGNMENT.ACCEPTED]: "待服务", [ASSIGNMENT.IN_SERVICE]: "服务中", [ASSIGNMENT.AWAITING_PHOTOS]: "待出片", [ASSIGNMENT.AFTER_SALE]: "售后处理中", [ASSIGNMENT.COMPLETED]: "已完成", [ASSIGNMENT.CLOSED]: "已结束", [ASSIGNMENT.ENDED]: "已结束" });
const SERVICE_LABELS = Object.freeze({ [SERVICE.WAITING_ACCEPTANCE]: "待接单", [SERVICE.WAITING_INVITATION_RESPONSE]: "等待邀约响应", [SERVICE.WAITING_INVITATION_PREREQUISITE]: "等待另一项邀约响应", [SERVICE.WAITING_ADMIN_REPLACEMENT]: "等待平台补位", [SERVICE.ACCEPTED]: "待服务", [SERVICE.IN_SERVICE]: "服务中", [SERVICE.AWAITING_PHOTOS]: "待出片", [SERVICE.COMPLETED]: "已完成", [SERVICE.CLOSED]: "已结束", [SERVICE.ENDED]: "已结束" });

const ACTION_CODES = Object.freeze({
    CONTACT_USER: 10, REGISTER_ARRIVAL: 20, CHECK_IN: 30, REPORT_USER_NO_SHOW: 40,
    REQUEST_COMPLETION_REVIEW: 50, CANCEL_SERVICE: 60, CREATE_DISPUTE: 70,
    VIEW_DISPUTE: 80, CREATE_AFTER_SALES: 90, VIEW_AFTER_SALE: 100,
});
const ACTION_NAMES = Object.freeze(Object.keys(ACTION_CODES).reduce((result, name) => {
    result[ACTION_CODES[name]] = name;
    return result;
}, {}));
const ACTION_LABELS = Object.freeze({
    10: "联系用户", 20: "我已到场", 30: "到场核销", 40: "用户爽约",
    50: "请求平台核实完成", 60: "退出承接", 70: "其他异常",
    80: "查看争议或提交证据", 90: "申请售后", 100: "查看售后",
});
const LIST_PRIMARY_ACTION_CODES = Object.freeze([
    ACTION_CODES.REGISTER_ARRIVAL, ACTION_CODES.CHECK_IN, ACTION_CODES.REQUEST_COMPLETION_REVIEW,
]);
const SETTLEMENT_LABELS = Object.freeze({ PENDING: "待结算", SETTLED: "已结算", FAILED: "结算异常" });
const ANOMALY_LABELS = Object.freeze({ NORMAL: "无异常", NONE: "无异常", ABNORMAL: "存在异常" });
const AFTER_SALE_LABELS = Object.freeze({ NONE: "无售后", PROCESSING: "处理中", COMPLETED: "已处理" });

const asText = (value) => value === null || value === undefined ? "" : String(value).trim();
const nullableText = (value) => {
    const text = asText(value);
    return text ? text : null;
};
const parseServerTime = (value) => {
    const text = asText(value);
    if (!text) return NaN;
    return Date.parse(`${text.replace(" ", "T")}+08:00`);
};
const timeText = (value) => {
    const text = asText(value);
    return text.length >= 16 ? text.slice(11, 16) : "--:--";
};
const roleMeta = (serviceType, roleKey) => ROLE_META[asText(serviceType).toUpperCase()]
    || (asText(roleKey) === "makeup" ? ROLE_META.MAKEUP : ROLE_META.PHOTOGRAPHY);
const isBlank = (value) => value === null || value === undefined
    || (typeof value === "string" && value.trim() === "");
const isCandidateProjection = (source) => !source.assignmentStatus && source.currentlyAssigned !== true;
const hasServiceOrderAmount = (source) => Object.prototype.hasOwnProperty.call(source, "serviceOrderAmount")
    && !isBlank(source.serviceOrderAmount)
    && Number.isFinite(Number(source.serviceOrderAmount));
const amountContractError = (source) => hasServiceOrderAmount(source)
    ? ""
    : "服务订单金额字段缺失或无效，请重新加载；如仍失败，请联系平台核对接口版本";
const normalizeAction = (raw) => {
    const source = raw && typeof raw === "object" ? raw : { code: raw };
    const rawCode = source.code === undefined ? source.action : source.code;
    const code = typeof rawCode === "number" ? rawCode : ACTION_CODES[asText(rawCode).toUpperCase()];
    if (!Object.prototype.hasOwnProperty.call(ACTION_NAMES, code)) return null;
    return {
        code,
        name: ACTION_NAMES[code],
        label: ACTION_LABELS[code],
        actionUrl: asText(source.actionUrl || source.url),
        disclosureNotice: asText(source.disclosureNotice),
    };
};

const normalizeCreatorServiceOrder = (raw = {}, summary = {}, options = {}) => {
    const source = { ...summary, ...raw };
    const summaryProjection = options.projection === "summary";
    const candidateProjection = isCandidateProjection(source);
    const meta = roleMeta(source.serviceType, source.roleKey);
    const serviceStatus = numericStatus(source.serviceStatus, SERVICE);
    const accepted = !candidateProjection && source.currentlyAssigned === true;
    const assignmentStatus = numericStatus(source.assignmentStatus, ASSIGNMENT);
    const contractError = amountContractError(source)
        || (summaryProjection || candidateProjection ? "" : creatorContractError(source));
    const serviceStartAt = asText(source.serviceStartAt || source.serviceAt);
    const serviceEndAt = asText(source.serviceEndAt);
    const theme = [source.themeTypeName, source.themeName, source.styleName].map(asText).filter(Boolean).join(" · ");
    const provinceName = asText(source.provinceName);
    const cityName = asText(source.cityName);
    const districtName = asText(source.districtName);
    const venueName = asText(source.venueName);
    const pointName = asText(source.pointName);
    const venueLocationSummary = asText(source.venueLocationSummary);
    const locationSummary = asText(source.locationSummary)
        || [provinceName, cityName, districtName, venueName, pointName].filter(Boolean).join(" · ");
    const contactEntry = source.contactEntry && typeof source.contactEntry === "object" ? source.contactEntry : {};
    // Compact list responses expose one current primary action, not a legacy action list.
    // Detail operations come from participant, fulfillment and case facts.
    const primaryAction = summaryProjection ? normalizeAction({ code: source.primaryActionCode }) : null;
    const primaryStageMatches = primaryAction && (
        ([ACTION_CODES.REGISTER_ARRIVAL, ACTION_CODES.CHECK_IN].includes(primaryAction.code) && assignmentStatus === ASSIGNMENT.ACCEPTED)
        || (primaryAction.code === ACTION_CODES.REQUEST_COMPLETION_REVIEW && assignmentStatus === ASSIGNMENT.IN_SERVICE));
    const safePrimaryAction = accepted && primaryStageMatches && LIST_PRIMARY_ACTION_CODES.includes(primaryAction.code)
        ? primaryAction : null;
    const contactable = !summaryProjection && accepted && [SERVICE.ACCEPTED, SERVICE.IN_SERVICE].includes(serviceStatus)
        && source.contactPhase === "CONTACTABLE" && source.invitationPrerequisitePending === false;
    const contactActionUrl = contactable ? asText(contactEntry.actionUrl) : "";
    const actions = [];
    const add = name => actions.push(normalizeAction(ACTION_CODES[name]));
    const rawCases = Array.isArray(source.disputes) ? source.disputes : (Array.isArray(source.cases) ? source.cases : []);
    const cases = rawCases.filter(record => record.serviceOrderNo === source.serviceOrderNo).map(record => {
        const value = record.status == null ? record.disputeStatus : record.status;
        const status = Number.isInteger(value) && value >= 1 && value <= 6 ? value : null;
        return { ...record, status, disputeStatus: status, closed: record.closed === true || [4, 5, 6].includes(status) };
    });
    if (summaryProjection) {
        if (accepted && safePrimaryAction) actions.push(safePrimaryAction);
    } else if (!candidateProjection) {
        const hasValidArrival = !isBlank(source.arrivalId) && !isBlank(source.arrivedAt) && !source.arrivalInvalidatedAt;
        if (contactable) actions.push({ ...normalizeAction(ACTION_CODES.CONTACT_USER), actionUrl: contactActionUrl });
        if (accepted && serviceStatus === SERVICE.ACCEPTED) {
            if (!source.invitationPrerequisitePending && source.currentServiceAssigned === true && source.photographyAssigned === true) {
                add(hasValidArrival ? "CHECK_IN" : "REGISTER_ARRIVAL");
            }
            add("CREATE_DISPUTE"); add("CANCEL_SERVICE");
        }
        if (accepted && serviceStatus === SERVICE.IN_SERVICE) { add("REQUEST_COMPLETION_REVIEW"); add("CREATE_DISPUTE"); }
        if (accepted && serviceStatus === SERVICE.AWAITING_PHOTOS) add("CREATE_DISPUTE");
        // The form explains the server's time gate; the browser never grants eligibility from its clock.
        if (accepted && serviceStatus === SERVICE.ACCEPTED && !source.invitationPrerequisitePending
            && hasValidArrival && !source.checkedInAt) add("REPORT_USER_NO_SHOW");
        if (cases.some(record => !record.closed && !(record.submissionSource === "SYSTEM" && record.allegation === "CREATOR_LATE"))) {
            for (let i = actions.length - 1; i >= 0; i--) {
                if ([ACTION_CODES.CREATE_DISPUTE, ACTION_CODES.REPORT_USER_NO_SHOW].includes(actions[i].code)) actions.splice(i, 1);
            }
        }
        cases.forEach(record => actions.push({ ...normalizeAction(record.disputeType === 'AFTER_SALES' ? ACTION_CODES.VIEW_AFTER_SALE : ACTION_CODES.VIEW_DISPUTE),
            label: record.closed ? "查看处理记录" : "查看处理进度",
            targetResourceType: "DISPUTE", targetResourceId: record.disputeNo }));
    }
    actions.forEach(action => {
        if (!action.targetResourceType) { action.targetResourceType = 'SERVICE_ORDER'; action.targetResourceId = asText(source.serviceOrderNo); }
    });
    if (contractError) actions.length = 0;
    const businessCode = asText(source.businessType || source.businessKey).toUpperCase();
    return {
        serviceOrderNo: asText(source.serviceOrderNo),
        roleKey: meta.roleKey,
        roleLabel: meta.roleLabel,
        serviceStatus,
        currentlyAssigned: source.currentlyAssigned,
        currentProvider: source.currentProvider ? {
            professionalType: source.currentProvider.professionalType,
            professionalTypeText: asText(source.currentProvider.professionalTypeText),
            professionalId: asText(source.currentProvider.professionalId),
            averageScore: source.currentProvider.averageScore == null ? null : Number(source.currentProvider.averageScore),
            reviewCount: Number(source.currentProvider.reviewCount) || 0,
            completedOrderCount: Number(source.currentProvider.completedOrderCount) || 0,
        } : null,
        reviewSummary: source.reviewSummary || null,
        contractError,
        assignmentStatus,
        statusLabel: (candidateProjection ? SERVICE_LABELS[serviceStatus] : STATUS_LABELS[assignmentStatus]) || "状态待确认",
        serviceAt: asText(source.serviceAt),
        expectedArrivalAt: asText(source.expectedArrivalAt),
        serviceStartAt,
        serviceEndAt,
        dateValue: serviceStartAt.slice(0, 10),
        timeText: serviceEndAt ? `${timeText(serviceStartAt)} - ${timeText(serviceEndAt)}` : timeText(serviceStartAt),
        acceptedAt: asText(source.acceptedAt),
        checkedInAt: nullableText(source.checkedInAt),
        finishedAt: nullableText(source.finishedAt),
        region: asText(source.regionSummary || source.region)
            || [provinceName, cityName, districtName].filter(Boolean).join(" · ") || "地区待确认",
        locationSummary,
        provinceName,
        cityName,
        districtName,
        venueName,
        pointName,
        venueLocationSummary,
        detailedLocation: accepted ? asText(source.detailedLocation || source.locationDetail || source.venueDetail) : "",
        theme: theme || asText(source.theme) || "服务主题待确认",
        packageName: asText(source.packageName),
        requirements: asText(source.requirements),
        businessKey: businessCode.toLowerCase(),
        businessLabel: asText(source.businessLabel) || (businessCode === "CAMPUS" ? "校园" : businessCode === "COMMERCIAL" ? "商业" : asText(source.themeTypeName) || "业务待确认"),
        customerDisplayName: asText(source.customerDisplayName || source.customerName || source.userDisplayName),
        serviceOrderAmount: source.serviceOrderAmount == null || !Number.isFinite(Number(source.serviceOrderAmount))
            ? null : Number(source.serviceOrderAmount),
        durationText: asText(source.durationText || source.estimatedDuration),
        progressHint: contractError || (summaryProjection ? asText(source.fulfillmentBlockingReason) : (accepted && serviceStatus === SERVICE.ACCEPTED ? invitationHint(source.invitationPrerequisitePending) : '') || asText(source.fulfillmentBlockingReason)),
        nextStepLabel: summaryProjection ? "" : contractError ? "重新加载" : ((actions.find(a => a.code !== ACTION_CODES.CONTACT_USER) || {}).label || "查看详情"),
        invitationPrerequisitePending: source.invitationPrerequisitePending,
        invitationResponseDeadline: nullableText(source.invitationResponseDeadline),
        contactPhase: source.contactPhase,
        acceptanceHistory: Array.isArray(source.acceptanceHistory) ? source.acceptanceHistory : [],
        cases,
        disputes: Array.isArray(source.disputes) ? source.disputes : [],
        availableActions: actions.map(action => action.code),
        primaryActionCode: summaryProjection ? (safePrimaryAction ? safePrimaryAction.code : null) : null,
        arrivalId: isBlank(source.arrivalId) ? null : Number(source.arrivalId),
        arrivedAt: nullableText(source.arrivedAt),
        arrivalInvalidatedAt: nullableText(source.arrivalInvalidatedAt),
        blockingServiceOrderNo: nullableText(source.blockingServiceOrderNo),
        lateMinutes: isBlank(source.lateMinutes) ? null : Number(source.lateMinutes),
        completionRequestedAt: source.completionRequestedAt,
        completionResolvedAt: source.completionResolvedAt,
        completionRequestReason: source.completionRequestReason,
        displayPriority: Number.isFinite(Number(source.displayPriority)) ? Number(source.displayPriority) : null,
        settlementStatusLabel: asText(source.settlementStatusLabel) || SETTLEMENT_LABELS[asText(source.settlementStatus).toUpperCase()] || "",
        settlementStatus: nullableText(source.settlementStatus),
        anomalyStatusLabel: asText(source.anomalyStatusLabel) || ANOMALY_LABELS[asText(source.anomalyStatus).toUpperCase()] || "",
        afterSaleStatusLabel: asText(source.afterSaleStatusLabel) || AFTER_SALE_LABELS[asText(source.afterSaleStatus).toUpperCase()] || "",
        anomalyStatus: asText(source.anomalyStatus).toUpperCase(),
        afterSaleStatus: asText(source.afterSaleStatus).toUpperCase(),
        actions,
        contactEntry: {
            actionUrl: contractError ? "" : contactActionUrl,
            disclosureNotice: accepted ? asText(contactEntry.disclosureNotice) : "",
        },
        candidateProjection,
    };
};

const positiveInteger = (value, fallback, maximum) => {
    const parsed = Math.floor(Number(value));
    if (!Number.isFinite(parsed) || parsed < 1) return fallback;
    return maximum ? Math.min(parsed, maximum) : parsed;
};

const normalizeCreatorServiceOrderPage = (raw = {}) => {
    const source = raw && typeof raw === "object" ? raw : {};
    const items = (Array.isArray(source.items) ? source.items : []).map((item) => {
        const listItem = {
        ...item,
        cases: Array.isArray(item && item.cases) ? item.cases : (Array.isArray(item && item.disputes) ? item.disputes : []),
        };
        return normalizeCreatorServiceOrder(listItem, {}, { projection: "summary" });
    });
    return {
        items,
        total: Math.max(0, Number(source.total) || 0),
        pageNo: positiveInteger(source.pageNo, 1),
        pageSize: positiveInteger(source.pageSize, 20, 50),
    };
};

const buildCreatorServiceOrderQuery = (filters = {}) => {
    const status = numericStatus(filters.status, ASSIGNMENT);
    const role = asText(filters.role).toLowerCase();
    const serviceType = role === "photographer" || role === "photography"
        ? "PHOTOGRAPHY"
        : role === "makeup" || role === "makeup_artist" ? "MAKEUP" : "";
    const serviceDate = asText(filters.serviceDate);
    const keyword = asText(filters.keyword);
    const sort = filters.sort === "accepted" ? "ACCEPTED_AT_DESC" : "SERVICE_AT_ASC";
    return {
        ...(status === null ? {} : { status }),
        ...(serviceType ? { serviceType } : {}),
        ...(serviceDate ? { serviceDateFrom: serviceDate, serviceDateTo: serviceDate } : {}),
        ...(keyword ? { keyword } : {}),
        sort,
        pageNo: positiveInteger(filters.pageNo, 1),
        pageSize: positiveInteger(filters.pageSize, 20, 50),
    };
};

const getMatchingCreatorServiceOrderDetail = (selected, serviceOrderNo) => selected
    && selected.serviceOrderNo === asText(serviceOrderNo) ? selected : null;

const filterCreatorServiceOrders = (orders, filters = {}) => {
    const role = asText(filters.role) || "all";
    const status = filters.status === "DISPUTE" ? "DISPUTE" : numericStatus(filters.status, ASSIGNMENT);
    const business = asText(filters.business).toLowerCase() || "all";
    const serviceDate = asText(filters.serviceDate);
    const keyword = asText(filters.keyword).toLowerCase();
    return (Array.isArray(orders) ? orders : []).filter((order) => (
        (role === "all" || order.roleKey === role)
        && (status === null || (status === "DISPUTE"
            ? (order.anomalyStatus && order.anomalyStatus !== "NONE") || (order.afterSaleStatus && order.afterSaleStatus !== "NONE")
            : order.assignmentStatus === status))
        && (business === "all" || order.businessKey === business)
        && (!serviceDate || order.dateValue === serviceDate)
        && (!keyword || [order.serviceOrderNo, order.customerDisplayName, order.locationSummary, order.region, order.detailedLocation, order.theme]
            .some((value) => asText(value).toLowerCase().includes(keyword)))
    ));
};

const sortCreatorServiceOrders = (orders, sort = "service") => [...(Array.isArray(orders) ? orders : [])]
    .sort((left, right) => {
        if (sort === "accepted") return parseServerTime(right.acceptedAt) - parseServerTime(left.acceptedAt);
        if (left.displayPriority !== null && right.displayPriority !== null) return left.displayPriority - right.displayPriority;
        return parseServerTime(left.serviceStartAt) - parseServerTime(right.serviceStartAt);
    });

const buildCreatorDisputePage = (orders, filters = {}) => {
    const pageNo = positiveInteger(filters.pageNo, 1);
    const pageSize = positiveInteger(filters.pageSize, 20, 50);
    const matched = sortCreatorServiceOrders(filterCreatorServiceOrders(orders, { ...filters, status: "DISPUTE" }), filters.sort);
    const offset = (pageNo - 1) * pageSize;
    return { items: matched.slice(offset, offset + pageSize), total: matched.length, pageNo, pageSize };
};

const groupCreatorSchedule = (orders) => {
    const occupyingStatuses = new Set([ASSIGNMENT.ACCEPTED, ASSIGNMENT.IN_SERVICE]);
    const grouped = new Map();
    (Array.isArray(orders) ? orders : [])
        .filter((order) => order.dateValue && order.currentlyAssigned !== false && (occupyingStatuses.has(order.assignmentStatus) || (order.candidateProjection && [SERVICE.ACCEPTED, SERVICE.IN_SERVICE].includes(order.serviceStatus))))
        .sort((left, right) => parseServerTime(left.serviceStartAt) - parseServerTime(right.serviceStartAt))
        .forEach((order) => {
            if (!grouped.has(order.dateValue)) grouped.set(order.dateValue, []);
            grouped.get(order.dateValue).push(order);
        });
    return [...grouped].map(([date, dateOrders]) => ({ date, orders: dateOrders }));
};

const getErrorStatus = (error) => Number(error && error.response && error.response.data && error.response.data.code
    || error && error.response && error.response.status
    || error && error.code
    || 0);
const isCreatorServiceOrderNotFound = (error) => getErrorStatus(error) === 404;
const getCreatorServiceOrderRequestState = (error) => {
    const code = getErrorStatus(error);
    return code === 401 ? CREATOR_SERVICE_ORDER_STATUS.SESSION_EXPIRED : CREATOR_SERVICE_ORDER_STATUS.ERROR;
};

module.exports = {
    ACTION_CODES,
    CREATOR_SERVICE_ORDER_STATUS,
    buildCreatorDisputePage,
    buildCreatorServiceOrderQuery,
    filterCreatorServiceOrders,
    getMatchingCreatorServiceOrderDetail,
    getCreatorServiceOrderRequestState,
    isCreatorServiceOrderNotFound,
    groupCreatorSchedule,
    normalizeCreatorServiceOrder,
    normalizeCreatorServiceOrderPage,
    sortCreatorServiceOrders,
};
